/**
 * Chat History API Client
 * Handles communication with the chat history backend API
 * 
 * API Routes (served by ACA runtime directly):
 * - GET  /runtime/webhooks/workflow/api/management/aiAssistantChatHistory/workflows/{workflowName}
 * - POST /runtime/webhooks/workflow/api/management/aiAssistantChatHistory/workflows/{workflowName}/save
 * - GET  /runtime/webhooks/workflow/api/management/aiAssistantChatHistory/workflows/{workflowName}/sessions/{sessionId}
 * - PUT /runtime/webhooks/workflow/api/management/aiAssistantChatHistory/workflows/{workflowName}/sessions/{sessionId}/rename
 * - DELETE /runtime/webhooks/workflow/api/management/aiAssistantChatHistory/workflows/{workflowName}/sessions/{sessionId}
 */

import { getLocalRuntimeUrl } from './localRuntimeClient';
import { appFetch, getAppUrl } from './appClient';
import { apiVersions } from './endpoints';

/**
 * Check if chat history should use local runtime.
 * Uses VITE_USE_LOCAL_CHAT_HISTORY_RUNTIME environment variable.
 *
 * Note: Named without "use" prefix to avoid ESLint react-hooks/rules-of-hooks
 * warning - this is NOT a React hook, just a simple env check.
 */
export const isLocalChatHistoryRuntime = (): boolean => {
  return import.meta.env.VITE_USE_LOCAL_CHAT_HISTORY_RUNTIME === 'true';
};

/**
 * Options for chat history API calls.
 * accessToken is required in both local and production modes.
 * subscriptionId, resourceGroup, and appName are only required in production mode.
 */
export interface ChatHistoryFetchOptions {
  projectName: string;
  appName: string;
  accessToken: string;
}

/**
 * Represents a conversation message
 */
export interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
  /**
   * Opaque per-message metadata serialized as a JSON string. Used today to
   * persist the per-turn pipeline-stage breakdown so a reloaded chat shows
   * the same collapsible Plan → Build → Validate cards the user saw
   * live. See {@link AssistantTurnMetadata} in `types/agui.ts` for the shape.
   */
  metadata?: string;
}

/**
 * Summary of a saved chat for list display
 */
export interface SavedChatSummary {
  id: string;
  name: string;
  workflowName: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  copilotSessionId?: string;
  contextUsagePercent?: number;
}

/**
 * Full saved chat with message history
 */
export interface SavedChat extends SavedChatSummary {
  messages: ConversationMessage[];
}

/**
 * Response from save chat API
 */
export interface SaveChatResponse {
  id: string;
  name: string;
  updatedAt: string;
}

/**
 * Response from list chats API
 */
export interface ListChatsResponse {
  chats: SavedChatSummary[];
  totalCount: number;
  maxChats: number;
}

/**
 * Response from delete chat API
 */
export interface DeleteChatResponse {
  success: boolean;
  message?: string;
}

/**
 * Response from rename chat API
 */
export interface RenameChatResponse {
  success: boolean;
  message?: string;
  id: string;
  name: string;
  updatedAt: string;
}

/**
 * Build the base URL for chat history APIs.
 * In local mode: Routes to localhost runtime.
 * In production: Routes directly to ACA FQDN runtime.
 */
const buildBaseUrl = (opts?: ChatHistoryFetchOptions): string => {
  if (isLocalChatHistoryRuntime()) {
    return `${getLocalRuntimeUrl()}/runtime/webhooks/workflow/api/management/aiAssistantChatHistory`;
  }
  
  if (!opts) {
    throw new Error('ChatHistoryFetchOptions required in production mode');
  }
  return `${getAppUrl(opts)}/aiAssistantChatHistory`;
};

/**
 * Append api-version query parameter for ARM requests.
 * Not needed in local mode.
 */
const appendApiVersion = (url: string): string => {
  if (isLocalChatHistoryRuntime()) {
    return url;
  }
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}api-version=${apiVersions.otto}`;
};

/**
 * Save or update a chat
 * @param workflowName - The workflow this chat belongs to
 * @param messages - The conversation messages to save
 * @param chatId - Optional: existing chat ID to update
 * @param opts - Fetch options with auth and app context
 * @param extra - Optional extra fields (copilotSessionId, contextUsagePercent)
 * @returns The saved chat info including ID and AI-generated name
 */
export async function saveChat(
  workflowName: string,
  messages: ConversationMessage[],
  chatId: string | undefined,
  opts?: ChatHistoryFetchOptions,
  extra?: { copilotSessionId?: string; contextUsagePercent?: number | null }
): Promise<SaveChatResponse> {
  const baseUrl = buildBaseUrl(opts);
  const url = appendApiVersion(`${baseUrl}/workflows/${encodeURIComponent(workflowName)}/save`);

  return appFetch<SaveChatResponse>(url, opts?.accessToken ?? '', {
    method: 'POST',
    body: JSON.stringify({
      id: chatId,
      workflowName,
      messages: JSON.stringify(messages),
      ...(extra?.copilotSessionId ? { copilotSessionId: extra.copilotSessionId } : {}),
      ...(extra?.contextUsagePercent != null ? { contextUsagePercent: extra.contextUsagePercent } : {}),
    }),
  });
}

/**
 * List all saved chats for a workflow
 * @param workflowName - The workflow name to list chats for
 * @param opts - Fetch options with auth and app context
 * @returns List of saved chat summaries
 */
export async function listChats(
  workflowName: string,
  opts?: ChatHistoryFetchOptions
): Promise<ListChatsResponse> {
  const baseUrl = buildBaseUrl(opts);
  const url = appendApiVersion(`${baseUrl}/workflows/${encodeURIComponent(workflowName)}`);

  return appFetch<ListChatsResponse>(url, opts?.accessToken ?? '');
}

/**
 * Get a specific chat by ID (includes full message history)
 * @param workflowName - The workflow name
 * @param chatId - The chat ID to retrieve
 * @param opts - Fetch options with auth and app context
 * @returns The full saved chat including messages
 */
export async function getChat(
  workflowName: string,
  chatId: string,
  opts?: ChatHistoryFetchOptions
): Promise<SavedChat> {
  const baseUrl = buildBaseUrl(opts);
  const url = appendApiVersion(`${baseUrl}/workflows/${encodeURIComponent(workflowName)}/sessions/${encodeURIComponent(chatId)}`);

  const data = await appFetch<SavedChat & { messages: string }>(url, opts?.accessToken ?? '');
  
  // Parse the messages JSON string back to an array
  return {
    ...data,
    messages: data.messages ? JSON.parse(data.messages) : [],
  };
}

/**
 * Delete a chat by ID
 * @param workflowName - The workflow name
 * @param chatId - The chat ID to delete
 * @param opts - Fetch options with auth and app context
 * @returns Delete operation result
 */
export async function deleteChat(
  workflowName: string,
  chatId: string,
  opts?: ChatHistoryFetchOptions
): Promise<DeleteChatResponse> {
  const baseUrl = buildBaseUrl(opts);
  const url = appendApiVersion(`${baseUrl}/workflows/${encodeURIComponent(workflowName)}/sessions/${encodeURIComponent(chatId)}`);

  try {
    return await appFetch<DeleteChatResponse>(url, opts?.accessToken ?? '', {
      method: 'DELETE',
    });
  } catch (error) {
    if (error instanceof Error && error.message.includes('404')) {
      return { success: false, message: `Chat not found: ${chatId}` };
    }
    throw error;
  }
}

/**
 * Rename a chat by ID
 * @param workflowName - The workflow name
 * @param chatId - The chat ID to rename
 * @param newName - The new name for the chat
 * @param opts - Fetch options with auth and app context
 * @returns Rename operation result
 */
export async function renameChat(
  workflowName: string,
  chatId: string,
  newName: string,
  opts?: ChatHistoryFetchOptions
): Promise<RenameChatResponse> {
  const baseUrl = buildBaseUrl(opts);
  const url = appendApiVersion(`${baseUrl}/workflows/${encodeURIComponent(workflowName)}/sessions/${encodeURIComponent(chatId)}/rename`);

  try {
    return await appFetch<RenameChatResponse>(url, opts?.accessToken ?? '', {
      method: 'PUT',
      body: JSON.stringify({ newName }),
    });
  } catch (error) {
    if (error instanceof Error && error.message.includes('404')) {
      return { success: false, message: `Chat not found: ${chatId}`, id: '', name: '', updatedAt: '' };
    }
    throw error;
  }
}
